import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ISurveyBuilderSurvey } from "../../../../../@types/SurveyBuilder";
import { ISurveyQuestion, QuestionTypes } from "../../../../../@types/Surveys";
import { IOption } from "../../../../../components/SelectDropdown";
import { surveyBuilderService } from "../../../../../services/surveyBuilderService";
import {
  flattenQuestions,
  singleSectionLayout,
  targetSectionId,
  toEditorQuestion,
  toQuestionRequest,
} from "../../../../../utils/surveyBuilderMapping";
import {
  AddButtons,
  IconButton,
  MutedText,
  Panel,
  PanelActions,
  PanelHeader,
  PanelTitle,
  PillButton,
  TitleGroup,
} from "../../SurveyBuilder.styles";
import { useConfirmAction } from "../ConfirmActionModal/ConfirmActionModal";
import { QuestionEditor } from "./Components/QuestionEditor";
import {
  QuestionSummary,
  QuestionTags,
} from "./Components/QuestionSummary/QuestionSummary";
import { SurveyDetailsCard } from "./Components/SurveyDetailsCard/SurveyDetailsCard";

const NEW_QUESTION = "new";

interface ISurveyBuilderContentTabProps {
  survey: ISurveyBuilderSurvey;
  languageCode: string;
  readOnly: boolean;
  surveyTypeLabel?: string;
  scoreStrategyOptions: IOption[];
  isSaving: boolean;
  run(
    action: () => Promise<ISurveyBuilderSurvey>,
    successMessage?: string
  ): Promise<ISurveyBuilderSurvey | undefined>;
}

/**
 * EN-5261: the survey card and one flat list of question cards. Each card saves on its own
 * endpoint. Sections are not shown: questions go into the version's (single) section.
 */
export function SurveyBuilderContentTab({
  survey,
  languageCode,
  readOnly,
  surveyTypeLabel,
  scoreStrategyOptions,
  isSaving,
  run,
}: ISurveyBuilderContentTabProps) {
  const { t } = useTranslation();
  const { version } = survey;
  const confirm = useConfirmAction();

  const [editingId, setEditingId] = useState<string>();
  const [newQuestionType, setNewQuestionType] = useState<QuestionTypes>();

  const questions = useMemo(() => flattenQuestions(version), [version]);
  const questionLabels = useMemo(
    () =>
      new Map(
        questions.map((q, index) => [
          q.id,
          q.type === "Informative"
            ? t("surveys_builder_content_infoPage")
            : t("surveys_builder_content_questionNumber", {
                number: index + 1,
              }),
        ])
      ),
    [questions, t]
  );
  const questionList = useMemo(
    () => questions.map((q) => ({ id: q.id, question: q.text ?? "" })),
    [questions]
  );

  const saveQuestion = async (question: ISurveyQuestion) => {
    if (editingId === NEW_QUESTION) {
      let sectionId: string | undefined = targetSectionId(version);
      if (!sectionId) {
        const withSection = await run(() =>
          surveyBuilderService.addSection(version.id, languageCode)
        );
        sectionId = withSection && targetSectionId(withSection.version);
        if (!sectionId) return false;
      }

      const created = await run(
        () =>
          surveyBuilderService.addQuestion(
            sectionId!,
            toQuestionRequest(question, languageCode)
          ),
        t("surveys_builder_toasts_questionSaved")
      );
      return !!created;
    }

    // The current row version: after a conflict the version was reloaded while the editor stayed open.
    const current = questions.find((q) => q.id === editingId);
    const saved = await run(
      () =>
        surveyBuilderService.updateQuestion(
          editingId!,
          toQuestionRequest(question, languageCode, current?.rowVersion)
        ),
      t("surveys_builder_toasts_questionSaved")
    );
    return !!saved;
  };

  const move = async (index: number, offset: number) => {
    const ids = questions.map((q) => q.id);
    [ids[index], ids[index + offset]] = [ids[index + offset], ids[index]];
    const { layout, emptySectionIds } = singleSectionLayout(version, ids);

    const reordered = await run(() =>
      surveyBuilderService.reorder(
        version.id,
        layout,
        version.rowVersion,
        languageCode
      )
    );
    // A survey imported with several sections becomes a single-section one.
    if (!reordered) return;
    // One at a time: parallel writes to the same draft would conflict.
    await emptySectionIds.reduce<Promise<unknown>>(
      (previous, sectionId) =>
        previous.then(() =>
          run(() => surveyBuilderService.deleteSection(sectionId, languageCode))
        ),
      Promise.resolve()
    );
  };

  const remove = (questionId: string) =>
    confirm({
      title: t("surveys_builder_content_deleteConfirm"),
      body: t("surveys_builder_content_deleteBody"),
      confirmLabel: t("surveys_builder_content_delete"),
      onConfirm: () =>
        run(
          () => surveyBuilderService.deleteQuestion(questionId, languageCode),
          t("surveys_builder_toasts_questionDeleted")
        ),
    });

  const startNew = (type?: QuestionTypes) => {
    setNewQuestionType(type);
    setEditingId(NEW_QUESTION);
  };

  const editor = (question?: ISurveyQuestion) => (
    <QuestionEditor
      key={question?.id ?? NEW_QUESTION}
      question={question}
      initialType={question ? undefined : newQuestionType}
      questionListforConditional={questionList}
      scoreStrategy={version.scoreStrategy ?? ""}
      domains={version.domains}
      isSaving={isSaving}
      onSave={saveQuestion}
      onClose={() => setEditingId(undefined)}
    />
  );

  return (
    <>
      <SurveyDetailsCard
        survey={survey}
        languageCode={languageCode}
        readOnly={readOnly}
        surveyTypeLabel={surveyTypeLabel}
        scoreStrategyOptions={scoreStrategyOptions}
        isSaving={isSaving}
        run={run}
      />

      {questions.map((question, index) => {
        const editorQuestion = toEditorQuestion(question);
        const isInfo = question.type === "Informative";

        return (
          <Panel question key={question.id}>
            <PanelHeader>
              <TitleGroup>
                <PanelTitle>
                  {isInfo
                    ? t("surveys_builder_content_infoPage")
                    : t("surveys_builder_content_questionNumber", {
                        number: index + 1,
                      })}
                </PanelTitle>
                {editingId !== question.id && (
                  <QuestionTags
                    question={question}
                    questionLabel={(id) => questionLabels.get(id)}
                  />
                )}
              </TitleGroup>
              {!readOnly && editingId !== question.id && (
                <PanelActions>
                  <IconButton
                    type="button"
                    aria-label={t("surveys_builder_content_moveUp")}
                    disabled={isSaving || index === 0}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </IconButton>
                  <IconButton
                    type="button"
                    aria-label={t("surveys_builder_content_moveDown")}
                    disabled={isSaving || index === questions.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </IconButton>
                  <IconButton
                    type="button"
                    disabled={isSaving || editingId !== undefined}
                    onClick={() => setEditingId(question.id)}
                  >
                    {t("surveys_builder_content_edit")}
                  </IconButton>
                  <IconButton
                    type="button"
                    onClick={() => remove(question.id)}
                    disabled={isSaving || !!question.code}
                    title={
                      question.code
                        ? t("surveys_builder_content_reservedCode")
                        : undefined
                    }
                  >
                    {t("surveys_builder_content_delete")}
                  </IconButton>
                </PanelActions>
              )}
            </PanelHeader>
            {editingId === question.id ? (
              editor(editorQuestion)
            ) : (
              <QuestionSummary
                question={question}
                questionLabel={(id) => questionLabels.get(id)}
              />
            )}
          </Panel>
        );
      })}

      {questions.length === 0 && editingId !== NEW_QUESTION && (
        <MutedText>{t("surveys_builder_content_noQuestions")}</MutedText>
      )}

      {editingId === NEW_QUESTION && <Panel question>{editor()}</Panel>}

      {!readOnly && editingId === undefined && (
        <AddButtons>
          <PillButton type="button" onClick={() => startNew()}>
            {t("surveys_builder_content_addQuestion")}
          </PillButton>
          <PillButton
            type="button"
            tone="light"
            onClick={() => startNew("Informative")}
          >
            {t("surveys_builder_content_addInfoPage")}
          </PillButton>
        </AddButtons>
      )}
    </>
  );
}
